import { DomAttribute } from 'targus';
import { getLocalizedAppointmentSummary } from '../../../../../../bookings/util/getLocalizedAppointmentSummary';
import {
  LinkTrackingData,
  SearchCardArea,
} from '../../../../../../common/AnalyticsContext/LinkTracking';
import { renderLinkTrackingAttributes } from '../../../../../../common/AnalyticsContext/renderLinkTrackingAttributes';
import { AppointmentLightBoxModal } from '../../../../../../common/components/AppointmentLightBoxModal/AppointmentLightBoxModal';
import { useSearchFilters } from '../../../../../../common/components/SearchHeader/filters/useSearchFilters';
import {
  COVID_19_FLU_VACCINATIONS,
  COVID_19_VACCINATIONS,
} from '../../../../../../common/components/SearchHeader/types/FilterChoice';
import { useToggle } from '../../../../../../common/hooks/useToggle';
import {
  COVAX_LIGHTBOX,
  useSimpleFlagr,
} from '../../../../../../common/services/FlagrService/useFlagr';
import { useMobile } from '../../../../../../common/services/screenSize/ScreenSizeService';
import { now } from '../../../../../../common/util/now';
import { suppressEvents } from '../../../../../../common/util/suppressEvents';
import { SearchResultAppointment, SearchResultAppointmentSummary } from '../../../../../types';
import {
  makePracticeBookingUrl,
  makePractitionerBookingUrl,
  makeAppointmentUrl,
  makeAppointmentLightboxUrl,
  makeCovaxLightboxUrl,
} from '../../../../../urls';
import { AppointmentsView, CtaLink } from './Appointments.styles';
import { CtaLinkWithTapTarget } from './CtaLinkWithExperiment';
import { NextAvailable } from './NextAvailable/NextAvailable';

interface Props {
  appointmentSummary: SearchResultAppointmentSummary | null;
  trackingData?: LinkTrackingData;
  specialty?: string;
  type: 'practice' | 'practitioner';
  timezone?: string;
  id: string;
  isTelehealthOnly?: boolean;
  profileUrl: string;
  name: string;
}

export const AppointmentSelector = ({
  appointmentSummary,
  id,
  name,
  profileUrl,
  specialty,
  trackingData,
  type,
  timezone,
  isTelehealthOnly,
}: Props) => {
  const mobile = useMobile();

  const [filters] = useSearchFilters();

  const [modalShowing, toggleModal] = useToggle(false);

  const firstAppointment: SearchResultAppointment | undefined =
    appointmentSummary?.firstAppointment;

  const localizedAppointmentSummary =
    isTelehealthOnly && appointmentSummary
      ? getLocalizedAppointmentSummary(appointmentSummary, timezone)
      : appointmentSummary;

  const restrictToPracticeId =
    type === 'practitioner' && filters.location ? firstAppointment?.practiceId : undefined;

  const vaccineType =
    filters.topic?.specialist === COVID_19_FLU_VACCINATIONS ? filters?.vaccineType : undefined;

  const { data: covaxLightbox } = useSimpleFlagr(COVAX_LIGHTBOX);

  const useCovaxLightbox = specialty === COVID_19_VACCINATIONS && covaxLightbox;
  const covaxLightboxUrl = makeCovaxLightboxUrl(id, filters.vaccineType);

  const composedUrl =
    type === 'practitioner'
      ? makePractitionerBookingUrl({
          defaultPractitionerUrl: profileUrl,
          mobile,
          practitionerId: id,
          practiceId: restrictToPracticeId,
          specialty,
          vaccineType,
        })
      : makePracticeBookingUrl(profileUrl, mobile, id, specialty, vaccineType);

  const allAvailabilitiesTracking = {
    ...trackingData,
    searchCardArea: SearchCardArea.ALL_AVAILABILITIES,
  };

  const iframeUrl = makeAppointmentLightboxUrl(id, specialty, filters.vaccineType);

  const handleClick: DomAttribute<'button', 'onClick'> = ev => {
    suppressEvents(ev);
    toggleModal();
  };

  const moreClickHandler = () => {
    toggleModal();
  };

  const shouldShowLightbox = !mobile && type === 'practice';

  return (
    <>
      <AppointmentsView>
        <NextAvailable
          appointmentSummary={localizedAppointmentSummary}
          nextUrl={firstAppointment && makeAppointmentUrl(firstAppointment, vaccineType)}
          moreUrl={useCovaxLightbox ? covaxLightboxUrl : composedUrl}
          trackingData={trackingData}
          currentDate={now()}
          onMoreClick={useCovaxLightbox ? moreClickHandler : undefined}
          opensLightbox={useCovaxLightbox}
        />

        {!shouldShowLightbox && (
          <CtaLinkWithTapTarget
            to={useCovaxLightbox ? covaxLightboxUrl : composedUrl}
            {...renderLinkTrackingAttributes(allAvailabilitiesTracking)}
          >
            {mobile ? 'See all' : 'All availabilities'}
          </CtaLinkWithTapTarget>
        )}

        {shouldShowLightbox && (
          <CtaLink
            as="button"
            onClick={handleClick}
            {...renderLinkTrackingAttributes(allAvailabilitiesTracking)}
          >
            All availabilities
          </CtaLink>
        )}
      </AppointmentsView>

      <AppointmentLightBoxModal
        isOpen={modalShowing}
        onRequestClose={toggleModal}
        iframeUrl={useCovaxLightbox ? covaxLightboxUrl : iframeUrl}
        headerText={name}
      />
    </>
  );
};
